import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Smartphone,
  Monitor,
  BarChart2,
} from 'lucide-react';
import api from '../../services/api';

const PER_PAGE = 20;

const SearchHistoryManagement = () => {
  const [activeTab, setActiveTab] = useState('recent');

  // Recent searches state
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [platformFilter, setPlatformFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Top searches state
  const [topTerms, setTopTerms] = useState([]);
  const [topLoading, setTopLoading] = useState(false);

  const fetchRecent = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: PER_PAGE };
      if (platformFilter) params.platform = platformFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await api.get('/admin/search-history', { params });
      setEntries(res.data.data || []);
      const p = res.data.pagination || {};
      setTotalPages(p.totalPages || 1);
      setTotal(p.total || 0);
    } catch (err) {
      console.error('Failed to fetch search history', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, platformFilter, dateFrom, dateTo]);

  const fetchTop = useCallback(async () => {
    setTopLoading(true);
    try {
      const res = await api.get('/admin/search-history/top', { params: { limit: 20 } });
      setTopTerms(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch top searches', err);
    } finally {
      setTopLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'recent') fetchRecent();
  }, [activeTab, fetchRecent]);

  useEffect(() => {
    if (activeTab === 'top') fetchTop();
  }, [activeTab, fetchTop]);

  useEffect(() => {
    setCurrentPage(1);
  }, [platformFilter, dateFrom, dateTo]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Search History</h1>
          <p className="text-gray-500 text-sm">Customer search queries from web and mobile</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('recent')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'recent'
              ? 'bg-[#548B2E] text-white'
              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          <Search className="w-4 h-4" />
          Recent Searches
        </button>
        <button
          onClick={() => setActiveTab('top')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'top'
              ? 'bg-[#548B2E] text-white'
              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          <TrendingUp className="w-4 h-4" />
          Top Searches
        </button>
      </div>

      {activeTab === 'recent' && (
        <div className="bg-white rounded-xl shadow-sm">
          {/* Filters */}
          <div className="p-4 border-b border-gray-100 flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Platform</label>
              <select
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]/30"
              >
                <option value="">All</option>
                <option value="web">Web</option>
                <option value="mobile">Mobile</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]/30"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]/30"
              />
            </div>
            {(platformFilter || dateFrom || dateTo) && (
              <button
                onClick={() => { setPlatformFilter(''); setDateFrom(''); setDateTo(''); }}
                className="text-xs text-gray-400 hover:text-gray-600 underline self-end pb-2"
              >
                Clear filters
              </button>
            )}
            <span className="self-end pb-2 text-xs text-gray-400 ml-auto">{total} result{total !== 1 ? 's' : ''}</span>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">Query</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">Platform</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">User</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">Date & Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {[140, 80, 100, 130].map((w, j) => (
                        <td key={j} className="px-4 py-3">
                          <div className={`h-4 bg-gray-100 rounded animate-pulse`} style={{ width: w }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : entries.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-gray-400">
                      No search records found.
                    </td>
                  </tr>
                ) : (
                  entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-800">{entry.query}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          entry.platform === 'mobile'
                            ? 'bg-purple-50 text-purple-700'
                            : 'bg-blue-50 text-blue-700'
                        }`}>
                          {entry.platform === 'mobile'
                            ? <Smartphone className="w-3 h-3" />
                            : <Monitor className="w-3 h-3" />}
                          {entry.platform}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {entry.user?.name || <span className="text-gray-300">Guest</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-400">{formatDate(entry.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-100 flex items-center justify-between">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <span className="text-sm text-gray-500">Page {currentPage} of {totalPages}</span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'top' && (
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-700 flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-[#548B2E]" />
              Most searched terms (all time)
            </h2>
          </div>
          {topLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" style={{ width: `${80 - i * 3}%` }} />
              ))}
            </div>
          ) : topTerms.length === 0 ? (
            <div className="p-10 text-center text-gray-400">No search data yet.</div>
          ) : (
            <div className="p-4 space-y-2">
              {topTerms.map((term) => {
                const maxCount = topTerms[0]?.count || 1;
                const pct = Math.round((term.count / maxCount) * 100);
                return (
                  <div key={term.rank} className="flex items-center gap-3">
                    <span className="text-xs font-bold text-gray-400 w-5 text-right">{term.rank}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-800">{term.query}</span>
                        <span className="text-xs text-gray-400">{term.count} search{term.count !== 1 ? 'es' : ''}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#548B2E] rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchHistoryManagement;
